import dayjs from 'dayjs'
import { useContext, useEffect, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import { payAndMakeEventBooking } from 'src/consumer/components/EventBooking/BookingHelperFunctions'
import { TickLottie } from 'src/consumer/components/EventBooking/EventBookingStyles'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { PublicAppFooter } from 'src/consumer/components/PublicAppFooter'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import PortalSwitcher from 'src/consumer/components/GlobalHeader/PortalSwitcher'
import { ClubbedAuthModal } from 'src/consumer/components/SignIn/ClubbedAuthComponents'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import confirmationTickLottie from 'src/consumer/lotties/confirmationTickLottie.json'
import {
  getDataWithoutParams,
  getPublicDataWithoutParams,
  postDataWithoutParams,
} from 'src/consumer/services/featureServices'
import { getImageLink, isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { CPBPOMBookAndPayButton, CPBPOMContainer } from './ClinicBrandPageStyles'

const WALKIN_BOOKING_SOURCE = 'clinic_walkin_qr'
// Distinct backend error codes from ClinicWalkinService.
const CODE_EXPIRED = 49081

export default function ClinicWalkinPage() {
  const navigate = useNavigate()
  const { token } = useParams()
  const { profileData, refreshProfileData } = useContext(ProfileContext)

  const [pageState, setPageState] = useState('resolving') // resolving | invalid | expired | booking | confirmed
  const [resolveError, setResolveError] = useState('')
  const [resolveData, setResolveData] = useState(null)

  // eventId -> { title, eventDetail, slots: [{date, availableTimeSlots}] }
  const [eventsById, setEventsById] = useState({})
  const [selectedEventId, setSelectedEventId] = useState(null)

  const [nextSlotsLoading, setNextSlotsLoading] = useState(false)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [bookingError, setBookingError] = useState(null)
  const [chosenSlot, setChosenSlot] = useState(null)

  const [stripePromise, setStripePromise] = useState(null)
  const [showClubbedAuthModal, setShowClubbedAuthModal] = useState(false)
  const [typeOfClubbedAuthModal, setTypeOfClubbedAuthModal] = useState('signup')
  const [initiatedBookingData, setInitiatedBookingData] = useState()
  const [bookedEventData, setBookedEventData] = useState()

  const pollRef = useRef(null)

  useEffect(() => {
    loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY).then(setStripePromise)
  }, [])

  // ---- Resolve the scanned daily token ----
  useEffect(() => {
    let cancelled = false
    async function resolve() {
      setPageState('resolving')
      const response = await postDataWithoutParams('/api/public/clinic-walkin/resolve', { token })
      if (cancelled) return
      const status = response?.status ?? response?.response?.status
      const body = response?.data ?? response?.response?.data
      if (status !== 200 || !isCommonResponseSuccessful(body?.code)) {
        if (body?.code === CODE_EXPIRED) {
          setResolveError(
            body?.message ||
              'This QR has expired. Please scan the latest QR shown at the clinic.',
          )
          setPageState('expired')
        } else {
          setResolveError(
            body?.message || 'This booking link is not valid. Please scan the QR at the clinic again.',
          )
          setPageState('invalid')
        }
        return
      }
      const data = body?.data ?? body
      setResolveData(data)
      const ids = [data?.defaultEventId, ...(data?.specialEventIds || [])].filter(Boolean)
      const fetched = await Promise.all(
        ids.map((eventId) => fetchEventBookingPage(data, eventId)),
      )
      if (cancelled) return
      const map = {}
      for (const item of fetched) {
        if (item) map[item.eventId] = item
      }
      setEventsById(map)
      setSelectedEventId(data?.defaultEventId)
      setPageState('booking')
    }
    if (token) resolve()
    return () => {
      cancelled = true
    }
  }, [token])

  async function fetchEventBookingPage(data, eventId) {
    const from = dayjs().startOf('month').format('YYYY-MM-DD')
    const to = dayjs().endOf('month').format('YYYY-MM-DD')
    const timeZone = encodeURIComponent(getTimezoneOffset())
    let query =
      `mentorSlug=${encodeURIComponent(data.mentorSlug)}` +
      `&eventId=${encodeURIComponent(eventId)}` +
      `&from=${from}&to=${to}&timeZone=${timeZone}`
    if (data.clinicLocationId) query += `&clinicId=${encodeURIComponent(data.clinicLocationId)}`
    let response = await getPublicDataWithoutParams(`/api/public/clinic-booking-page?${query}`)
    if (response?.response) {
      response = { status: response.response.status, data: response.response.data }
    }
    if (response?.status !== 200) return null
    const bookingPage = (response?.data?.data ?? response?.data)?.bookingPage
    if (!bookingPage?.event) return null
    const eventDetail = {
      ...bookingPage.event,
      // Ensure the booking carries clinic context (so the existing in-clinic
      // booking-confirmation WhatsApp + appointment number fire) and the
      // walk-in provenance (so the patient record is created self-validated).
      contextId: bookingPage.event.contextId || data.clinicLocationId,
      clinicBrandId: bookingPage.event.clinicBrandId || data.clinicBrandId,
      bookingSource: WALKIN_BOOKING_SOURCE,
    }
    const slots = Array.isArray(bookingPage.availableSlots)
      ? bookingPage.availableSlots.map(({ date, slots: s }) => ({
          date,
          availableTimeSlots: Array.isArray(s) ? s : [],
        }))
      : []
    return { eventId, title: bookingPage.event.title, eventDetail, slots }
  }

  // ---- Poll the selected event's slots every 20s while booking ----
  useEffect(() => {
    if (pageState !== 'booking' || !resolveData || !selectedEventId) return
    async function refresh() {
      setNextSlotsLoading(true)
      const item = await fetchEventBookingPage(resolveData, selectedEventId)
      if (item) {
        setEventsById((prev) => ({ ...prev, [selectedEventId]: item }))
      }
      setNextSlotsLoading(false)
    }
    pollRef.current = setInterval(refresh, 20000)
    return () => clearInterval(pollRef.current)
  }, [pageState, resolveData, selectedEventId])

  const selectedEvent = selectedEventId ? eventsById[selectedEventId] : null
  const todaysSlots =
    selectedEvent?.slots?.find((item) => item?.date === dayjs().format('YYYY-MM-DD'))
      ?.availableTimeSlots || []

  // Core booking call, parameterised by the profile to use. The default reads
  // from ProfileContext; the auth callback passes the freshly-fetched profile
  // so we can resume booking immediately on the same screen instead of letting
  // the modal's post-login navigator hijack the flow.
  async function proceedWithBooking(profileToUse) {
    const slotToBook = todaysSlots?.[0]
    if (!slotToBook?.startTime || !slotToBook?.endTime) {
      setBookingError('That slot just filled up. Please wait for the next available slot.')
      return
    }
    if (!selectedEvent?.eventDetail) {
      setBookingError('Could not load this session. Please try again.')
      return
    }
    setBookingError(null)
    setChosenSlot(slotToBook)
    setPaymentLoading(true)
    await payAndMakeEventBooking(
      selectedEvent.eventDetail, // bookingEventDetails (carries bookingSource + clinic context)
      {
        data: { startTime: slotToBook.startTime, endTime: slotToBook.endTime },
        postBufferMins: 0,
        preBufferMins: 0,
      },
      'razorpay',
      'GFT-default',
      stripePromise,
      resolveData?.mentorId,
      profileToUse,
      () => null,
      () => setPageState('confirmed'),
      'uid',
      'bookNextAvailable',
      (data) => setInitiatedBookingData(data),
    )
    setPaymentLoading(false)
  }

  async function handleBookButtonClick() {
    if (isTokenInvalidOrAbsent(localStorage.getItem('token'))) {
      setTypeOfClubbedAuthModal('signup')
      setShowClubbedAuthModal(true)
      return
    }
    await proceedWithBooking(profileData)
  }

  // Called by ClubbedAuthModal on successful login/signup. Providing this
  // callback prevents the modal's default finishPostLoginNavigation from
  // window.location.href-redirecting the user to /portals — the walk-in flow
  // owns the page and completes the booking inline.
  async function handleAuthenticated(freshProfile) {
    try {
      // Mark the page as the active consumer flow so any wellness/portal auto-
      // navigation guards leave us alone.
      sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
      sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
      sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
      localStorage.removeItem('lastChosenFlow')
    } catch {
      /* private-browsing: handled downstream */
    }
    setShowClubbedAuthModal(false)
    // Best-effort sync the global context so future renders see the user.
    try {
      if (typeof refreshProfileData === 'function') {
        refreshProfileData({ _forceRefresh: true })
      }
    } catch (e) {
      console.warn('Walk-in: refreshProfileData failed (non-fatal):', e?.message)
    }
    // Resume booking inline with the freshly-fetched profile.
    await proceedWithBooking(freshProfile || profileData)
  }

  async function getAndSetBookedEventData() {
    const bookedEventId = initiatedBookingData?.bookedEvent?.id
    if (!bookedEventId) return
    for (let attempt = 0; attempt < 5; attempt++) {
      const res = await getDataWithoutParams(
        `/api/secure/user/booked-slot/details?eventId=${bookedEventId}`,
      )
      if (res?.status === 200 && isCommonResponseSuccessful(res?.data?.code)) {
        const fetched = res?.data?.data?.bookedEvent
        setBookedEventData(fetched)
        if (fetched?.appointmentNumber) return
      }
      await new Promise((r) => setTimeout(r, 1500))
    }
  }

  useEffect(() => {
    if (pageState === 'confirmed' && !bookedEventData) {
      getAndSetBookedEventData()
    }
  }, [pageState])

  function resetForNextPatient() {
    setBookedEventData(undefined)
    setInitiatedBookingData(undefined)
    setChosenSlot(null)
    setBookingError(null)
    setSelectedEventId(resolveData?.defaultEventId)
    setPageState('booking')
  }

  const eventOptions = resolveData
    ? [resolveData.defaultEventId, ...(resolveData.specialEventIds || [])].filter(Boolean)
    : []

  return (
    <>
      <PublicAppHeader config={{ showLinks: false }} />
      <ClubbedAuthModal
        open={showClubbedAuthModal}
        onClose={() => setShowClubbedAuthModal(false)}
        loginVariant={typeOfClubbedAuthModal === 'login'}
        nameFieldHeading="Patient Name"
        onAuthenticated={handleAuthenticated}
      />

      {pageState === 'resolving' && <LoadingSpinner bgColor="#F5F5F599" dark />}

      {(pageState === 'invalid' || pageState === 'expired') && (
        <CPBPOMContainer>
          <div style={{ fontSize: '40px', marginTop: '40px' }}>
            {pageState === 'expired' ? '⏱️' : '⚠️'}
          </div>
          <div style={{ fontWeight: 600, marginTop: '10px', textAlign: 'center' }}>
            {pageState === 'expired' ? 'QR Expired' : 'Invalid Link'}
          </div>
          <div style={{ marginTop: '10px', textAlign: 'center', maxWidth: '320px', fontSize: '14px' }}>
            {resolveError}
          </div>
        </CPBPOMContainer>
      )}

      {pageState === 'booking' && resolveData && (
        <CPBPOMContainer>
          <div>{resolveData.clinicName}</div>
          <div style={{ marginTop: '10px' }}>Book a session with</div>
          <div>
            <b>{resolveData.mentorName}</b>
          </div>
          {resolveData.mentorProfilePicId && (
            <img
              style={{ width: '150px', borderRadius: '16px', margin: '5px 0' }}
              src={getImageLink(resolveData.mentorProfilePicId)}
              alt="practitioner"
            />
          )}

          {eventOptions.length > 1 && (
            <div style={{ marginTop: '16px', width: '100%', maxWidth: '320px' }}>
              <div style={{ fontSize: '12px', marginBottom: '6px' }}>Choose session type:</div>
              <select
                value={selectedEventId || ''}
                onChange={(e) => {
                  setBookingError(null)
                  setSelectedEventId(e.target.value)
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #c6c6c6' }}
              >
                {eventOptions.map((id) => (
                  <option key={id} value={id}>
                    {eventsById[id]?.title || 'Session'}
                    {id === resolveData.defaultEventId ? '' : ' (special)'}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ fontWeight: 500, fontSize: '20px', marginTop: '24px' }}>
            {dayjs().format('DD-MMM-YYYY')}
          </div>
          <div style={{ fontSize: '14px' }}>Today</div>
          <div style={{ marginTop: '10px' }}>
            {'Next Available Slot '}
            {nextSlotsLoading && <Spinner animation="border" variant="dark" size="sm" />}
          </div>

          {todaysSlots.length > 0 ? (
            <>
              <div>
                <b>{dayjs(todaysSlots[0]?.startTime).format('h:mm A')}</b>
              </div>
              <CPBPOMBookAndPayButton
                style={{ marginTop: '10px' }}
                onClick={handleBookButtonClick}
                disabled={paymentLoading}
              >
                {'Pay and Book'}
                {paymentLoading && <Spinner animation="border" variant="light" size="sm" />}
              </CPBPOMBookAndPayButton>
            </>
          ) : (
            <div>
              {nextSlotsLoading ? (
                <Spinner animation="border" variant="dark" size="sm" />
              ) : (
                <div style={{ fontSize: '10px' }}>No Slots Available</div>
              )}
            </div>
          )}

          {bookingError && (
            <div
              style={{
                color: '#c0392b',
                fontSize: '12px',
                marginTop: '10px',
                textAlign: 'center',
                maxWidth: '300px',
              }}
            >
              {bookingError}
            </div>
          )}

          <div
            style={{
              marginTop: '24px',
              fontSize: '12px',
              textAlign: 'center',
              width: '300px',
            }}
          >
            Note: Appointments might be delayed due to existing appointments or emergencies.
          </div>
        </CPBPOMContainer>
      )}

      {pageState === 'confirmed' && (
        <CPBPOMContainer>
          <TickLottie animationData={confirmationTickLottie} loop={false} autoPlay={true} />
          <div>
            <b>Session Confirmed</b>
          </div>
          <div style={{ marginTop: '8px' }}>
            {resolveData?.mentorName}
            {selectedEvent?.title ? ` · ${selectedEvent.title}` : ''}
          </div>
          {bookedEventData?.appointmentNumber && (
            <>
              <div style={{ marginTop: '24px' }}>Your Appointment Number is</div>
              <div style={{ fontSize: '50px' }}>
                <b>{bookedEventData.appointmentNumber}</b>
              </div>
            </>
          )}
          {chosenSlot?.startTime && (
            <div style={{ marginTop: '10px' }}>
              Appointment time: <b>{dayjs(chosenSlot.startTime).format('h:mm A')}</b>
            </div>
          )}
          <CPBPOMBookAndPayButton style={{ marginTop: '30px' }} onClick={resetForNextPatient}>
            Book Next Patient
          </CPBPOMBookAndPayButton>
          <div
            onClick={() => navigate('/my-live-sessions')}
            style={{ marginTop: '24px', cursor: 'pointer' }}
          >
            <u>View your booked sessions</u>
          </div>
          <div
            style={{
              marginTop: '24px',
              paddingTop: '16px',
              borderTop: '1px solid #e5e7eb',
              width: '100%',
              maxWidth: '320px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Continue in another portal:</div>
            <PortalSwitcher darkText />
          </div>
        </CPBPOMContainer>
      )}

      <PublicAppFooter />
    </>
  )
}
